import { useCallback, useContext, ReactElement, useState, useEffect } from 'react';
import {
  Application,
  Calculation,
  CalculationDetails,
  CalculationDialogMode,
  CalculationStatus,
  CalculationType,
  Fasteners,
  LoadCalcType,
  Loads,
  ProductDetails,
  SpansAndLoads,
  Standard,
  Standards,
  TemperatureDetails,
  Zone,
  ZoneDetails,
} from '../../types/domainTypes';
import { Box, Grid, Stack, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import { AppContainer } from '../../components/AppContainer/AppContainer';
import { Header } from '../../components/Header/Header';
import AppBodyContainer from 'components/AppBodyContainer/AppBodyContainer';
import { StatusLabel } from '../Projects/StatusLabel';
import { TemperatureDetailsDialog } from './TemperatureDetailsDialog';
import {
  useDeleteCalculationMutation,
  useDeleteFastenersMutation,
  useDeleteTemperatureMutation,
  useDeleteZoneMutation,
  useGetCalculationQuery,
  useGetProjectQuery,
  useSaveCalculationDetailsMutation,
  useSaveCalculationMutation,
  useSaveCalculationSpansAndLoadsMutation,
  useSaveCalculationStandardMutation,
  useSaveFastenersDetailsMutation,
  useSaveProductDetailsMutation,
  useSaveTemperatureDetailsMutation,
  useSaveZoneDetailsMutation,
} from 'redux/services/projects';
import { useGetTemperatureDataQuery } from '../../redux/services/temperatures';
import CenteredComponentView from 'components/CenteredComponentView/CenteredComponentView';
import { StandardDialog } from 'pages/ProjectDashboard/ProjectStandardsDialog';
import { useGetStandardsQuery } from 'redux/services/standards';
import { SpansAndLoadDialog } from './SpansAndLoadDialog';
import { CalculationStatusNotification } from './CalculationStatusNotification';
import { ProductDetailsDialog } from './ProductDetailsDialog';
import { useGetDivisionsQuery } from 'redux/services/products';
import { CalculationDetailsView } from './CalculationDetailsView';
import { CalculationStandardsView } from './CalculationStandardsView';
import { TemperatureView } from './TemperatureView';
import { SpansAndLoadsView } from './SpansAndLoadsView';
import { ProductDetailsView } from './ProductDetailsView';
import { CalculationResultsPanel } from './CalculationResultsPanel';
import { SkeletonPage } from '../../components/SkeletonPage/SkeletonPage';
import { DropdownOption, MoreDropdown } from 'components/Menu/MoreDropdown';
import { ConfirmationDialog } from 'components/ConfirmationDialog/ConfirmationDialog';
import { MenuButton } from 'components/MenuButton/MenuButton';
import { useCalculateMutation } from 'redux/services/calculations';
import { FastenersDetailsDialog } from 'pages/Calculation/FastenersDetailsDialog';
import { CalculationContext } from './CalculationContextProvider';
import { FastenersView } from './FastenersView';
import { NotFoundPage } from 'pages/NotFound/NotFoundPage';
import CalculationInputCards from 'components/organisms/CalculationInputCards/CalculationInputCards';
import ZonesDialog from 'components/organisms/ZonesDialog/ZonesDialog';
import { ZonesView } from './ZonesView';
import { AppBaseButton } from 'components/AppBaseButton/AppBaseButton';
import { headerHeight } from 'components/Header/Header';
import { breadcrumbHeight } from 'components/AppBreadcrumbs/AppBreadcrumbs';
import { duplicateCalculationData } from 'common/duplicateMethods';
import { CalculationDialog } from 'pages/ProjectDashboard/ProjectCalculationsDialog';
import { useGetSupportWidthsQuery } from 'redux/services/supportWidths';

enum CalculationRequest {
  PanelCheck = 'pcheck',
  PanelAndFastenerCheck = 'pandfcheck',
  FastenerCheck = 'fcheck',
}

function CalculationPage(): ReactElement {
  const projectId = useParams<'projectId'>().projectId as string;
  const calculationId = useParams<'calculationId'>().calculationId as string;
  const { calculation, setCalculation } = useContext(CalculationContext);
  const { data: project, refetch: refetchProject } = useGetProjectQuery(projectId || '');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoadingFinish, setIsLoadingFinish] = useState(false);
  const [isUpdatingResult, setIsUpdatingResult] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [isDeleteCalculationConfirmationOpen, setIsDeleteCalculationConfirmationOpen] = useState(false);
  const [isDeleteFastenersConfirmationOpen, setIsDeleteFastenersConfirmationOpen] = useState(false);
  const [isDeleteTemperatureConfirmationOpen, setIsDeleteTemperatureConfirmationOpen] = useState(false);
  const [isDeleteZoneConfirmationOpen, setIsDeleteZoneConfirmationOpen] = useState(false);
  const [isEditDetailsDialogOpen, setIsEditDetailsDialogOpen] = useState(false);
  const [isEditStandardDialogOpen, setIsEditStandardDialogOpen] = useState(false);
  const [isTemperatureDetailsDialogOpen, setTemperatureDetailsDialogOpen] = useState(false);
  const [isSpansAndLoadDialogDialogOpen, setSpansAndLoadDialogDialogOpen] = useState(false);
  const [isProductDetailsDialogOpen, setProductDetailsDialogOpen] = useState(false);
  const [isFastenersDetailsDialogOpen, setFastenersDetailsDialogOpen] = useState(false);
  const [isZonesDialogOpen, setZonesDialogOpen] = useState(false);
  const [isCalculateErrorDialogOpen, setCalculateErrorDialogOpen] = useState(false);
  const [calculateErrorDialogText, setCalculateErrorDialogText] = useState<string>('');
  const [isResultsPanelOpen, setResultsPanelOpen] = useState(false);
  const [previousCalculationStatus, setPreviousCalculationStatus] = useState(CalculationStatus.InProgress);
  const [hasCalcRun, setHasCalcRun] = useState(false);
  const [zoneToEdit, setZoneToEdit] = useState<Zone>();
  const [zoneIdToRemove, setZoneIdToRemove] = useState('');
  const [addNewZone, setAddNewZone] = useState(false);
  const [isDuplicateCalculationConfirmationOpen, setIsDuplicateCalculationConfirmationOpen] = useState(false);
  const [isCalculationDuplicated, setIsCalculationDuplicated] = useState(false);
  const [isDuplicateDetailsOpen, setIsDuplicateDetailsOpen] = useState(false);
  const [calculationToDuplicate, setCalculationToDuplicate] = useState<Calculation>();

  const standards = useGetStandardsQuery(null);
  const standardOptions = standards.data as Standards[];
  const { data: supportWidthOptions } = useGetSupportWidthsQuery(null);

  const calculationRes = useGetCalculationQuery(
    { projectId: projectId, calculationId: calculationId },
    { refetchOnMountOrArgChange: true }
  );
  useEffect(() => {
    if (calculationRes.error) {
      enqueueSnackbar(t('calculation-page.error-message-load-calculation'), {
        variant: 'error',
      });

      if ('status' in calculationRes.error && calculationRes.error.status === 404) {
        setIsNotFound(true);
      }
    } else if (calculationRes.isSuccess) {
      setCalculation(calculationRes.data);
    }
    return () => setIsLoadingFinish(true);
  }, [calculationRes.data, calculationRes.error, calculationRes.isSuccess, enqueueSnackbar, setCalculation, t]);

  useEffect(() => {
    setIsUpdatingResult(false);
  }, [calculation?.result?.timeRetrieved]);

  const temperature = useGetTemperatureDataQuery(null);
  useEffect(() => {
    if (temperature.error) {
      enqueueSnackbar(t('calculation-page.error-message-load-temperature'), {
        variant: 'error',
      });
    }
  }, [t, enqueueSnackbar, temperature]);

  const division = useGetDivisionsQuery(null);
  useEffect(() => {
    if (division.error) {
      enqueueSnackbar(t('calculation-page.error-message-load-division'), {
        variant: 'error',
      });
    }
  }, [t, enqueueSnackbar, division.error]);

  const [saveCalculationDetails, calculationDetails] = useSaveCalculationDetailsMutation();
  useEffect(() => {
    if (calculationDetails.error) {
      enqueueSnackbar(t('calculation-page.error-message-edit-calculation-details'), {
        variant: 'error',
      });
    }
  }, [enqueueSnackbar, calculationDetails, t]);

  const [saveTemperatureDetails, saveTemperature] = useSaveTemperatureDetailsMutation();
  useEffect(() => {
    if (saveTemperature.error) {
      enqueueSnackbar(t('calculation-page.error-message-saving-temperature-details'), {
        variant: 'error',
      });
    } else if (saveTemperature.isSuccess) {
      setTemperatureDetailsDialogOpen(false);
    }
  }, [t, enqueueSnackbar, saveTemperature, setTemperatureDetailsDialogOpen]);

  const [editStandard, saveStandard] = useSaveCalculationStandardMutation();
  useEffect(() => {
    if (saveStandard.error) {
      enqueueSnackbar(`${t('calculation-page.error-message-save-standard')} ${calculationId} `, {
        variant: 'error',
      });
    } else if (saveStandard.isSuccess) {
      setIsEditStandardDialogOpen(false);
    }
  }, [calculationId, saveStandard, enqueueSnackbar, t, setIsEditStandardDialogOpen]);

  const [saveSpansAndLoads, saveSpansAndLoadsResult] = useSaveCalculationSpansAndLoadsMutation();
  useEffect(() => {
    if (
      calculation?.details.application === Application.RoofPanel &&
      calculation?.spansAndLoads &&
      calculation?.product?.selfWeight !== calculation.spansAndLoads.permanentLoads?.selfWeight
    ) {
      const updateSpansAndLoads = async (data: SpansAndLoads) => {
        await saveSpansAndLoads({ spansAndLoads: data, projectId: projectId, calculationId: calculation.id });
      };
      const updatedSpansAndLoads = {
        ...calculation.spansAndLoads,
        loadsCalculationType: LoadCalcType.RoofPanel,
        permanentLoads: {
          ...calculation.spansAndLoads.permanentLoads,
          selfWeight: calculation?.product?.selfWeight,
        },
      };
      updateSpansAndLoads(updatedSpansAndLoads as SpansAndLoads);
    }
  }, [
    calculation?.product?.selfWeight,
    calculation?.spansAndLoads,
    calculation?.details.application,
    saveSpansAndLoads,
    projectId,
    calculation?.id,
  ]);

  useEffect(() => {
    if (saveSpansAndLoadsResult.error) {
      enqueueSnackbar(`${t('calculation-page.error-message-save-spans-and-loads')} ${calculationId} `, {
        variant: 'error',
      });
    } else if (saveSpansAndLoadsResult.isSuccess) {
      setSpansAndLoadDialogDialogOpen(false);
    }
  }, [calculationId, enqueueSnackbar, t, saveSpansAndLoadsResult, setSpansAndLoadDialogDialogOpen]);

  const [saveZoneDetails, saveZoneDetailsResult] = useSaveZoneDetailsMutation();
  useEffect(() => {
    if (saveZoneDetailsResult.error) {
      enqueueSnackbar(t('calculation-page.error-message-saving-zone-details'), { variant: 'error' });
    } else if (saveZoneDetailsResult.isSuccess) {
      setZonesDialogOpen(false);
      setAddNewZone(false);
      setZoneToEdit(undefined);
    }
  }, [enqueueSnackbar, t, saveZoneDetailsResult, setZonesDialogOpen, setAddNewZone, setZoneToEdit]);

  const [saveProductDetails, saveProduct] = useSaveProductDetailsMutation();
  useEffect(() => {
    if (saveProduct.error) {
      enqueueSnackbar(`${t('calculation-page.error-message-saving-product-details')} ${calculationId} `, {
        variant: 'error',
      });
    } else if (saveProduct.isSuccess) {
      setProductDetailsDialogOpen(false);
    }
  }, [
    calculationId,
    t,
    enqueueSnackbar,
    saveProduct,
    setProductDetailsDialogOpen,
    calculation,
    calculation?.product?.externalFaceThickness,
    calculation?.product?.panelType,
  ]);

  const [saveFastenersDetails, saveFastenersResult] = useSaveFastenersDetailsMutation();
  useEffect(() => {
    if (saveFastenersResult.error) {
      enqueueSnackbar(`${t('calculation-page.error-message-saving-fasteners-details')} ${calculationId} `, {
        variant: 'error',
      });
      console.log('saveFasteners error:', saveFastenersResult.error);
    } else if (saveFastenersResult.isSuccess) {
      setFastenersDetailsDialogOpen(false);
    }
  }, [calculationId, t, enqueueSnackbar, saveFastenersResult, setFastenersDetailsDialogOpen]);

  const [deleteCalculation, deleteCalculationResult] = useDeleteCalculationMutation();
  useEffect(() => {
    if (deleteCalculationResult.error) {
      enqueueSnackbar(t('calculation-page.error-message-delete-calculation', { calculationName: calculation?.details.name }), {
        variant: 'error',
      });
    } else if (deleteCalculationResult.isSuccess) {
      if (projectId) {
        navigate(`/project/${projectId}`);
      }
    }
  }, [projectId, calculation?.details.name, deleteCalculationResult, t, navigate, enqueueSnackbar, setProductDetailsDialogOpen]);

  const [deleteFasteners, deleteFastenersResult] = useDeleteFastenersMutation();
  useEffect(() => {
    if (deleteFastenersResult.error) {
      enqueueSnackbar(t('calculation-page.error-message-delete-fasteners'), {
        variant: 'error',
      });
    }
  }, [projectId, calculation?.details.name, deleteFastenersResult, t, navigate, enqueueSnackbar, setProductDetailsDialogOpen]);

  const [deleteTemperature, deleteTemperatureResult] = useDeleteTemperatureMutation();
  useEffect(() => {
    if (deleteTemperatureResult.error) {
      enqueueSnackbar(t('calculation-page.error-message-delete-temperature'), {
        variant: 'error',
      });
    }
  }, [projectId, calculation?.details.name, deleteTemperatureResult, t, navigate, enqueueSnackbar, setProductDetailsDialogOpen]);

  const [deleteZone, deleteZoneResult] = useDeleteZoneMutation();
  useEffect(() => {
    if (deleteZoneResult.error) {
      enqueueSnackbar(t('calculation-page.error-message-delete-zone'), {
        variant: 'error',
      });
    }
  }, [deleteZoneResult, t, enqueueSnackbar]);

  const setCalculationStatus = useCallback(
    (calculationStatus: CalculationStatus) => {
      setCalculation((calc: any) => {
        if (calc) {
          return { ...calc, status: calculationStatus };
        }
      });
    },
    [setCalculation]
  );

  useEffect(() => {
    if (calculation?.status === CalculationStatus.FailedToCalculate && hasCalcRun) {
      enqueueSnackbar(
        `${t('calculation-page.error-message-calculate', {
          calculationName: calculation?.details.name,
          id: calculationId,
        })}`,
        {
          variant: 'error',
        }
      );
    }
  }, [calculation?.status, enqueueSnackbar, t, hasCalcRun, calculation?.details.name, calculationId]);

  const [calculate, calculateResult] = useCalculateMutation();
  useEffect(() => {
    if (calculateResult.error) {
      enqueueSnackbar(
        `${t('calculation-page.error-message-start-calculate', {
          calculationName: calculation?.details.name,
          id: calculationId,
        })}`,
        {
          variant: 'error',
        }
      );

      setCalculationStatus(previousCalculationStatus);
      setIsUpdatingResult(false);
    }
  }, [
    calculateResult.error,
    enqueueSnackbar,
    t,
    setCalculationStatus,
    calculation?.details.name,
    calculationId,
    previousCalculationStatus,
  ]);

  const [duplicateCalculation, duplicateCalculationDetails] = useSaveCalculationMutation();
  useEffect(() => {
    if (duplicateCalculationDetails.error) {
      enqueueSnackbar(t('calculation-page.error-message-duplicate-calculation'), {
        variant: 'error',
      });
    } else if (duplicateCalculationDetails.isSuccess && isCalculationDuplicated && !calculationToDuplicate?.zoneDetails?.zones.length) {
      const newCalculationId = duplicateCalculationDetails.data?.id;
      if (projectId) {
        navigate(`/project/${projectId}/calculation/${newCalculationId}`);
        setIsCalculationDuplicated(false);
        setIsDuplicateDetailsOpen(false);
        refetchProject();
      }
    }
  }, [
    refetchProject,
    isCalculationDuplicated,
    duplicateCalculationDetails.data?.id,
    duplicateCalculationDetails.error,
    duplicateCalculationDetails.isSuccess,
    projectId,
    calculation?.details.name,
    deleteCalculationResult,
    t,
    navigate,
    enqueueSnackbar,
    setProductDetailsDialogOpen,
    calculationToDuplicate?.zoneDetails?.zones.length,
  ]);

  const [duplicateZoneDetails, duplicateZoneDetailsResult] = useSaveZoneDetailsMutation();
  useEffect(() => {
    const newCalculationId = duplicateCalculationDetails.data?.id;
    const duplicatedCalculationId = duplicateZoneDetailsResult.originalArgs?.calculationId;
    if (duplicateZoneDetailsResult.error && isCalculationDuplicated && duplicatedCalculationId === newCalculationId) {
      enqueueSnackbar(t('calculation-page.error-message-duplicate-calculation-zones'), { variant: 'error' });
      navigate(`/project/${projectId}/calculation/${newCalculationId}`);
      setIsCalculationDuplicated(false);
    } else if (duplicateZoneDetailsResult.isSuccess && isCalculationDuplicated) {
      navigate(`/project/${projectId}/calculation/${newCalculationId}`);
      setIsCalculationDuplicated(false);
    }
  }, [enqueueSnackbar, t, duplicateZoneDetailsResult, duplicateCalculationDetails, navigate, projectId, isCalculationDuplicated]);

  const onCloseZonesDialog = () => {
    setZonesDialogOpen(false);
    setAddNewZone(false);
    setZoneToEdit(undefined);
  };

  const onEditDetails = async (updatedCalculationDetails: CalculationDetails) => {
    if (calculation) {
      const newCalculationDetails = {
        ...calculation.details,
        name: updatedCalculationDetails.name,
        description: updatedCalculationDetails.description,
      } as CalculationDetails;

      await saveCalculationDetails({
        calculationDetails: newCalculationDetails,
        projectId: projectId,
        calculationId: calculation.id,
      });
      calculationRes.refetch();
      refetchProject();
    }
  };

  const onEditStandardSave = async (standard: Standard) => {
    if (projectId === undefined || calculationId === undefined) return;

    await editStandard({ standard, calculationId, projectId });
    calculationRes.refetch();
  };

  const onTemperatureDetailsSave = async (temperatureDetails: TemperatureDetails) => {
    if (calculation) {
      await saveTemperatureDetails({ temperatureDetails, projectId: projectId, calculationId: calculation.id });
    }
    calculationRes.refetch();
  };

  const onSpansAndLoadDialogSave = async (data: SpansAndLoads) => {
    if (calculation) {
      await saveSpansAndLoads({ spansAndLoads: data, projectId: projectId, calculationId: calculation.id });
    }
    calculationRes.refetch();
  };

  const onProductDetailsSave = async (productDetails: ProductDetails, shouldDeleteFasteners = false) => {
    if (calculation) {
      await saveProductDetails({ productDetails, projectId: projectId, calculationId: calculation.id });
      if (shouldDeleteFasteners) {
        await deleteFasteners({ projectId: projectId, calculationId: calculation.id });
      }
    }
    calculationRes.refetch();
  };

  const onFastenersDetailsSave = async (fastenersDetails: Fasteners) => {
    if (calculation) {
      await saveFastenersDetails({ fastenersDetails, projectId: projectId, calculationId: calculation.id });
    }
    console.log('refetching fastener details...');
    calculationRes.refetch();
  };

  const onZoneDetailsSave = async (zoneDetails: ZoneDetails) => {
    if (calculation) {
      const zoneDetailsToSave = { ...zoneDetails };

      if (calculation.zoneDetails) {
        if (addNewZone) {
          zoneDetailsToSave.zones = [...calculation.zoneDetails.zones, ...zoneDetails.zones];
        }

        if (zoneToEdit) {
          zoneDetailsToSave.zones = calculation.zoneDetails.zones.map((zone) =>
            zone.id !== zoneDetails.zones[0].id ? zone : zoneDetails.zones[0]
          );
        }
      }

      await saveZoneDetails({ zoneDetails: zoneDetailsToSave, projectId, calculationId: calculation.id });
    }
    calculationRes.refetch();
  };

  const onDeleteCalculation = async () => {
    if (calculation) {
      await deleteCalculation({ projectId: projectId, calculationId: calculation.id });
      setIsDeleteCalculationConfirmationOpen(false);
    }
  };

  const onDeleteFasteners = async () => {
    if (calculation) {
      await deleteFasteners({ projectId: projectId, calculationId: calculation.id });
      setIsDeleteFastenersConfirmationOpen(false);
      calculationRes.refetch();
    }
  };

  const onDeleteTemperature = async () => {
    if (calculation) {
      await deleteTemperature({ projectId: projectId, calculationId: calculation.id });
      setIsDeleteTemperatureConfirmationOpen(false);
      calculationRes.refetch();
    }
  };

  const handleOnClickAddZone = () => {
    setAddNewZone(true);
    setZonesDialogOpen(true);
  };

  const handleOnClickEditZone = (zone: Zone) => {
    setZoneToEdit(zone);
    setZonesDialogOpen(true);
  };

  const handleOnClickRemoveZone = (zoneId: string) => {
    setZoneIdToRemove(zoneId);
    setIsDeleteZoneConfirmationOpen(true);
  };

  const onDeleteZone = async () => {
    if (calculation) {
      await deleteZone({ projectId: projectId, calculationId: calculation.id, zoneId: zoneIdToRemove });
      setIsDeleteZoneConfirmationOpen(false);
      calculationRes.refetch();
    }
  };

  const handleCloseDuplicateDetailsDialog = () => {
    setIsDuplicateDetailsOpen(false);
  };

  const onDuplicateSave = async (calculationToSaveDetails: CalculationDetails) => {
    if (projectId && calculationToDuplicate) {
      const newCalculation = {
        ...calculationToDuplicate,
        details: calculationToSaveDetails,
        zoneDetails: undefined,
      } as Calculation;
      await duplicateCalculation({ calculation: newCalculation, projectId }).then(async (res) => {
        const calc = res as { data: Calculation };
        if (calc.data) {
          handleCloseDuplicateDetailsDialog();
          setIsCalculationDuplicated(true);

          const zoneDetailsToSave = {
            zones: calculationToDuplicate?.zoneDetails?.zones.map((zoneDetails) => {
              const { id, ...details } = zoneDetails;
              return details;
            }),
          } as ZoneDetails;

          if (calculationToDuplicate?.zoneDetails) {
            await duplicateZoneDetails({ zoneDetails: zoneDetailsToSave, projectId, calculationId: calc.data.id });
          }
        }
      });
    }
  };

  const onDuplicateCalculation = async () => {
    if (calculation && project) {
      setCalculationToDuplicate(duplicateCalculationData(calculation, project?.standard?.id));
      setIsDuplicateCalculationConfirmationOpen(false);
      setIsDuplicateDetailsOpen(true);
    }
  };

  if (isNotFound) {
    return <NotFoundPage />;
  }

  if (calculationRes.isLoading || !calculation || temperature.isLoading || division.isLoading || standards.isLoading) {
    return <SkeletonPage isCalculationDashboard />;
  }

  if (isLoadingFinish && !calculation) {
    return <CenteredComponentView>{t('calculation-page.error-message-not-found')}</CenteredComponentView>;
  }

  const loadHasData = (load?: Loads) => {
    return !!(
      load?.uniformLoad ||
      (load?.distributedLoads && load?.distributedLoads.length > 0) ||
      (load?.pointLoads && load?.pointLoads.length > 0)
    );
  };

  const calcHasLoads = (calc: Calculation) => {
    let hasLoads = false;

    if (
      loadHasData(calc.spansAndLoads?.pressureLoads) ||
      loadHasData(calc.spansAndLoads?.suctionLoads) ||
      loadHasData(calc.spansAndLoads?.permanentLoads) ||
      loadHasData(calc.spansAndLoads?.snowLoads) ||
      loadHasData(calc.spansAndLoads?.excepSnowLoads) ||
      loadHasData(calc.spansAndLoads?.imposedLoads) ||
      loadHasData(calc.spansAndLoads?.constructionLoads) ||
      calc.temperature
    ) {
      hasLoads = true;
    }

    return hasLoads;
  };

  const handleCalculate = (calcReq: CalculationRequest) => {
    if (calcReq === CalculationRequest.FastenerCheck) {
      if (!(calculation.zoneDetails && calculation.zoneDetails.zones.length > 0 && calculation.product)) {
        setCalculateErrorDialogText(t('calculation-page.calculate-error-dialog.fastener-check-text'));
        setCalculateErrorDialogOpen(true);
        return;
      }
    } else {
      if (
        !(
          calculation.spansAndLoads &&
          calculation.product &&
          calcHasLoads(calculation) &&
          (calcReq === CalculationRequest.PanelCheck ||
            (calcReq === CalculationRequest.PanelAndFastenerCheck &&
              calculation.fasteners?.pullOutDesignResistance &&
              calculation.fasteners?.pullOverDesignResistance))
        )
      ) {
        if (calcReq === CalculationRequest.PanelCheck) {
          setCalculateErrorDialogText(t('calculation-page.calculate-error-dialog.text'));
        } else {
          setCalculateErrorDialogText(t('calculation-page.calculate-error-dialog.fastener-text'));
        }
        setCalculateErrorDialogOpen(true);
        return;
      }
    }

    setIsUpdatingResult(true);
    setPreviousCalculationStatus(calculation.status);
    setCalculationStatus(CalculationStatus.Calculating);
    setResultsPanelOpen(true);

    calculate({
      projectId: projectId || '',
      calculationId: calculationId || '',
      includeFasteners: calcReq === CalculationRequest.PanelAndFastenerCheck,
    });

    setHasCalcRun(true);
  };

  const handleResultsPanelToggle = () => {
    setResultsPanelOpen(!isResultsPanelOpen);
  };

  return (
    <AppContainer header={<Header />}>
      <AppBodyContainer data-testid="calculationPage" style={{ paddingTop: 9 + breadcrumbHeight }}>
        <CalculationResultsPanel
          calculation={calculation}
          projectId={projectId}
          supportWidth={calculation.supportWidth}
          onRecalculate={() =>
            handleCalculate(
              calculation.details.type === CalculationType.FastenerCheck
                ? CalculationRequest.FastenerCheck
                : calculation.fasteners
                ? CalculationRequest.PanelAndFastenerCheck
                : CalculationRequest.PanelCheck
            )
          }
          panelOpen={isResultsPanelOpen}
          handleToggle={handleResultsPanelToggle}
          isUpdatingResult={isUpdatingResult}
        />
        <Stack rowGap={3}>
          <CalculationStatusNotification
            projectId={projectId || ''}
            calculations={calculation}
            setCalculations={setCalculation}
            refetchCalculation={calculationRes.refetch}
          />
          <Box
            style={{
              position: 'sticky',
              top: headerHeight + breadcrumbHeight,
              paddingTop: 15,
              backgroundColor: '#F4F5F7',
              zIndex: 1,
              marginLeft: -5,
              marginRight: -5,
              paddingLeft: 5,
              paddingRight: 5,
            }}
          >
            <Grid container direction="row" justifyContent="space-between" alignItems="center" columnSpacing={1} sx={{ pb: 1.88 }}>
              <Grid item sx={{ flex: 1 }}>
                <Typography variant="h1" sx={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ paddingRight: '20px' }}>{calculation.details.name}</span>
                  <StatusLabel size="medium" value={calculation.status} isCalculationStatus />
                </Typography>
              </Grid>
              <Grid item>
                {calculation.details.type === CalculationType.PanelCheck ? (
                  <MenuButton
                    disabled={calculation.status === CalculationStatus.Calculating}
                    buttonText={t('calculation-page.calculate-button')}
                    buttonVariant={'contained'}
                    options={[
                      {
                        value: CalculationRequest.PanelCheck,
                        text: t('calculation-page.label-panel-check'),
                      },
                      {
                        value: CalculationRequest.PanelAndFastenerCheck,
                        text: t('calculation-page.label-panel-and-fastener-check'),
                        disabled: !calculation.fasteners,
                      },
                    ]}
                    onMenuItemClicked={(value) => handleCalculate(value as CalculationRequest)}
                  />
                ) : (
                  <AppBaseButton
                    variant="contained"
                    disabled={calculation.status === CalculationStatus.Calculating}
                    onClick={() => handleCalculate(CalculationRequest.FastenerCheck)}
                  >
                    {t('calculation-page.calculate-button')}
                  </AppBaseButton>
                )}
              </Grid>
              <Grid item>
                <MoreDropdown data-testid="calculation-page-dropdown">
                  <DropdownOption
                    callback={() => setIsDeleteCalculationConfirmationOpen(true)}
                    label={t('calculation-page.label-delete-calculation')}
                  />
                  <DropdownOption
                    callback={() => {
                      setIsDuplicateCalculationConfirmationOpen(true);
                    }}
                    label={t('project-details-page.label-duplicate-calculation')}
                  />
                </MoreDropdown>
              </Grid>
            </Grid>
          </Box>

          <CalculationDetailsView
            calculation={calculation}
            onEditDetails={onEditDetails}
            isEditDetailsDialogOpen={isEditDetailsDialogOpen}
            setIsEditDetailsDialogOpen={setIsEditDetailsDialogOpen}
          />

          <CalculationStandardsView standard={calculation.standard} setIsEditStandardDialogOpen={setIsEditStandardDialogOpen} />

          <Stack sx={{ pb: '10px', pt: '30px' }}>
            <Typography variant="h2">{t('calculation-page.label-calculation-input')}</Typography>
            <Typography variant="subtitle1">{t('calculation-page.label-calculation-input-caption')}</Typography>
          </Stack>

          <CalculationInputCards
            calculation={calculation}
            onClickProduct={() => setProductDetailsDialogOpen(true)}
            onClickSpansAndLoads={() => setSpansAndLoadDialogDialogOpen(true)}
            onClickTemperature={() => setTemperatureDetailsDialogOpen(true)}
            onClickFasteners={() => setFastenersDetailsDialogOpen(true)}
            onClickZones={() => setZonesDialogOpen(true)}
          />

          {calculation.product && (
            <ProductDetailsView onEditClick={() => setProductDetailsDialogOpen(true)} productDetails={calculation.product} />
          )}
          {calculation.spansAndLoads && (
            <SpansAndLoadsView
              onEditClick={() => setSpansAndLoadDialogDialogOpen(true)}
              spansAndLoads={calculation.spansAndLoads}
              isWallCalculation={calculation.details.application === Application.WallPanel}
            />
          )}
          <TemperatureView
            onDeleteClick={() => setIsDeleteTemperatureConfirmationOpen(true)}
            onEditClick={() => setTemperatureDetailsDialogOpen(true)}
            temperature={calculation.temperature}
          />
          <FastenersView
            onEditClick={() => setFastenersDetailsDialogOpen(true)}
            onDeleteClick={() => setIsDeleteFastenersConfirmationOpen(true)}
            fasteners={calculation.fasteners}
            fromManufacturerSpecs={true}
          />
          {calculation.zoneDetails && (
            <ZonesView
              zoneDetails={calculation.zoneDetails}
              onClickAdd={handleOnClickAddZone}
              onClickEdit={handleOnClickEditZone}
              onClickRemove={handleOnClickRemoveZone}
            />
          )}
        </Stack>
        {isEditStandardDialogOpen && (
          <StandardDialog
            onSave={onEditStandardSave}
            data={calculation.standard}
            options={standardOptions}
            supportWidthOptions={supportWidthOptions}
            onClose={() => setIsEditStandardDialogOpen(false)}
            title={t('calculation-page.standard-dialog.edit-standard-header')}
          />
        )}
        {isTemperatureDetailsDialogOpen && (
          <TemperatureDetailsDialog
            temperatureDetails={calculation.temperature}
            temperatureData={temperature.data}
            title={
              calculation.temperature
                ? t('calculation-page.temperature-details-dialog.edit-title')
                : t('calculation-page.temperature-details-dialog.title')
            }
            onClose={() => setTemperatureDetailsDialogOpen(false)}
            onSave={onTemperatureDetailsSave}
          />
        )}
        {isSpansAndLoadDialogDialogOpen && (
          <SpansAndLoadDialog
            spansAndLoadsDetails={calculation.spansAndLoads}
            onClose={() => setSpansAndLoadDialogDialogOpen(false)}
            onSave={onSpansAndLoadDialogSave}
            application={calculation.details.application}
          />
        )}
        {isProductDetailsDialogOpen && (
          <ProductDetailsDialog
            divisionData={division.data}
            application={calculation.details.application}
            productDetails={calculation.product}
            fasteners={calculation.fasteners}
            onClose={() => setProductDetailsDialogOpen(false)}
            onSave={onProductDetailsSave}
          />
        )}
        {isFastenersDetailsDialogOpen && (
          <FastenersDetailsDialog
            fasteners={calculation.fasteners}
            fastenerCheck={calculation.details.type === CalculationType.FastenerCheck}
            onClose={() => setFastenersDetailsDialogOpen(false)}
            onSave={onFastenersDetailsSave}
            productCheck={!!calculation.product}
          />
        )}
        {isZonesDialogOpen && (
          <ZonesDialog zone={zoneToEdit} newZone={addNewZone} onClose={onCloseZonesDialog} onSave={onZoneDetailsSave} />
        )}
        {isDeleteCalculationConfirmationOpen && (
          <ConfirmationDialog
            isOpen={isDeleteCalculationConfirmationOpen}
            isFullscreen={false}
            handleClose={() => setIsDeleteCalculationConfirmationOpen(false)}
            handleConfirm={onDeleteCalculation}
            titleIcon={<WarningAmberIcon />}
            title={t('calculation-page.delete-confirmation.title')}
            questionText={t('calculation-page.delete-confirmation.question-text', { calculationName: calculation.details.name })}
            cancelText={t('calculation-page.delete-confirmation.cancel-text')}
            confirmText={t('calculation-page.delete-confirmation.confirm-text')}
          />
        )}
        {isDeleteFastenersConfirmationOpen && (
          <ConfirmationDialog
            data-testid="fasteners-delete-confirmation"
            isOpen={isDeleteFastenersConfirmationOpen}
            isFullscreen={false}
            handleClose={() => setIsDeleteFastenersConfirmationOpen(false)}
            handleConfirm={onDeleteFasteners}
            titleIcon={<WarningAmberIcon />}
            title={t('calculation-page.delete-fasteners-confirmation.title')}
            questionText={t('calculation-page.delete-fasteners-confirmation.question-text')}
            cancelText={t('calculation-page.delete-fasteners-confirmation.cancel-text')}
            confirmText={t('calculation-page.delete-fasteners-confirmation.confirm-text')}
          />
        )}
        {isDeleteTemperatureConfirmationOpen && (
          <ConfirmationDialog
            isOpen={isDeleteTemperatureConfirmationOpen}
            isFullscreen={false}
            handleClose={() => setIsDeleteTemperatureConfirmationOpen(false)}
            handleConfirm={onDeleteTemperature}
            titleIcon={<WarningAmberIcon />}
            title={t('calculation-page.delete-temperature-confirmation.title')}
            questionText={t('calculation-page.delete-temperature-confirmation.question-text')}
            cancelText={t('calculation-page.delete-temperature-confirmation.cancel-text')}
            confirmText={t('calculation-page.delete-temperature-confirmation.confirm-text')}
          />
        )}
        {isDeleteZoneConfirmationOpen && (
          <ConfirmationDialog
            isOpen={isDeleteZoneConfirmationOpen}
            isFullscreen={false}
            handleClose={() => setIsDeleteZoneConfirmationOpen(false)}
            handleConfirm={onDeleteZone}
            titleIcon={<WarningAmberIcon />}
            title={t('calculation-page.delete-zone-confirmation.title')}
            questionText={t('calculation-page.delete-zone-confirmation.question-text')}
            cancelText={t('calculation-page.delete-zone-confirmation.cancel-text')}
            confirmText={t('calculation-page.delete-zone-confirmation.confirm-text')}
          />
        )}
        {isCalculateErrorDialogOpen && (
          <ConfirmationDialog
            isOpen={isCalculateErrorDialogOpen}
            handleClose={() => setCalculateErrorDialogOpen(false)}
            title={t('calculation-page.calculate-error-dialog.title')}
            questionText={calculateErrorDialogText}
            handleConfirm={() => setCalculateErrorDialogOpen(false)}
            isFullscreen={false}
            confirmText={t('calculation-page.calculate-error-dialog.confirm-text')}
            titleIcon={<WarningAmberIcon />}
          />
        )}
        {isDuplicateCalculationConfirmationOpen && (
          <ConfirmationDialog
            isOpen={isDuplicateCalculationConfirmationOpen}
            isFullscreen={false}
            handleClose={() => setIsDuplicateCalculationConfirmationOpen(false)}
            handleConfirm={onDuplicateCalculation}
            titleIcon={<WarningAmberIcon />}
            title={t('calculation-page.duplicate-confirmation.title')}
            questionText={t('calculation-page.duplicate-confirmation.question-text', { calculationName: calculation.details.name })}
            cancelText={t('calculation-page.duplicate-confirmation.cancel-text')}
            confirmText={t('calculation-page.duplicate-confirmation.confirm-text')}
          />
        )}
        {isDuplicateDetailsOpen && (
          <CalculationDialog
            onSave={onDuplicateSave}
            onClose={handleCloseDuplicateDetailsDialog}
            calculation={calculationToDuplicate}
            mode={CalculationDialogMode.Duplicate}
          />
        )}
      </AppBodyContainer>
    </AppContainer>
  );
}

export default CalculationPage;
